import React, { createContext, useState, useContext, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Check if user is authenticated on mount
    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        try {
            const token = localStorage.getItem('token');
            console.log('🔍 Checking auth, token exists:', !!token);
            
            if (token) {
                // For Laravel Sanctum tokens, we don't decode JWT
                // Instead, we verify by fetching user data from the backend
                console.log('📡 Fetching user data from API...');
                const response = await axiosInstance.get('/user');

                console.log('✅ API Response:', response.data);

                if (response.data) {
                    const userData = response.data;
                    
                    // Extract role from roles array (first role)
                    const userRole = userData.roles && userData.roles.length > 0 
                        ? userData.roles[0].name 
                        : null;
                    
                    // Set user data with extracted role
                    const userWithRole = {
                        ...userData,
                        role: userRole
                    };
                    
                    console.log('👤 User authenticated:', userWithRole.name, 'Role:', userRole);
                    setUser(userWithRole);
                    setIsAuthenticated(true);
                } else {
                    console.warn('⚠️ No data in response, logging out');
                    logout();
                }
            } else {
                console.log('❌ No token found in localStorage');
            }
        } catch (error) {
            console.error('❌ Auth check failed:', error);
            console.error('Error response:', error.response);
            console.error('Error status:', error.response?.status);
            
            // Only logout if it's an authentication error (401)
            if (error.response && error.response.status === 401) {
                console.log('🚪 401 error - logging out');
                logout();
            } else {
                console.log('⚠️ Non-401 error, keeping user logged in but stopping loading');
                // For other errors, just set loading to false
                setLoading(false);
            }
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password) => {
        try {
            const response = await axiosInstance.post('/login', {
                email,
                password
            });

            if (response.data && response.data.token) {
                const { token, user: userData } = response.data;
                
                // Store only the token in localStorage
                localStorage.setItem('token', token);
                
                // Extract role from roles array (first role)
                const userRole = userData.roles && userData.roles.length > 0 
                    ? userData.roles[0].name 
                    : null;
                
                // Set user data in state with extracted role
                const userWithRole = {
                    ...userData,
                    role: userRole
                };
                
                setUser(userWithRole);
                setIsAuthenticated(true);
                
                return { success: true };
            }
            
            return { success: false, message: 'Login failed' };
        } catch (error) {
            console.error('Login error:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Login failed' 
            };
        }
    };

    const logout = async () => {
        try {
            const token = localStorage.getItem('token');
            if (token) {
                // Call logout endpoint to invalidate token on server
                await axiosInstance.post('/logout');
            }
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Clear local state regardless of API call result
            localStorage.removeItem('token');
            setUser(null);
            setIsAuthenticated(false);
        }
    };

    const hasRole = (roles) => {
        if (!user || !user.role) return false;
        
        if (Array.isArray(roles)) {
            return roles.includes(user.role);
        }
        
        return user.role === roles;
    };

    const hasPermission = (permission) => {
        if (!user || !user.permissions) return false;
        return user.permissions.includes(permission);
    };

    const value = {
        user,
        isAuthenticated,
        loading,
        login,
        logout,
        checkAuth,
        hasRole,
        hasPermission
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
